import React, {useState} from 'react';

import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {SelectBaseRTK} from 'app/components/general/SelectBase/SelectBaseRTK';
import {
  convertLocationParamsToFetchParams,
  useDocuments,
} from 'app/documents/api';
import {type BaseItemPickerObject} from 'app/general/widgets/SimpleItemPickerList/SimpleItemPickerList';
import StateButton from 'app/general/widgets/StateButton';
import {
  useBulkAddDocumentsToTeam,
  useBulkRemoveDocumentsFromTeam,
  useCreateTeams,
} from 'app/teams/api';
import {AddNewTeamMemberButton} from 'app/teams/components/TeamDetailPage/TeamMembersSubPage/AddNewTeamMemberButton';
import {TeamMembersSubPage} from 'app/teams/components/TeamDetailPage/TeamMembersSubPage/TeamMembersSubPage';
import {HOME_WELCOME_INVITATIONS} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {DocumentListItem} from './Imports';
import {SmallTextSpan, WelcomeStructure} from './WelcomeStructure';

import type {DocumentFetchParamsType} from 'app/documents/types';
import type {WithRouterProps} from 'react-router';
import type {DocumentSerializedType, TeamSerializedType} from 'sweet-types';

export const WelcomeDashboard = (props: WithRouterProps) => (
  <WelcomeStructure {...props} currentStep={1}>
    {({onNext}) => <SweetSuperchargeTeams onNext={onNext} />}
  </WelcomeStructure>
);

const SUGGESTED_TEAMS: Array<BaseItemPickerObject> = [
  {
    name: 'Management',
    url: 'management',
    content_type: 'team',
    description: '',
  },
  {name: 'Marketing', url: 'marketing', content_type: 'team', description: ''},
  {name: 'Sales', url: 'sales', content_type: 'team', description: ''},
  {
    name: 'Operations',
    url: 'operations',
    content_type: 'team',
    description: '',
  },
  {
    name: 'Accounting',
    url: 'accounting',
    content_type: 'team',
    description: '',
  },
  {name: 'HR', url: 'hr', content_type: 'team', description: ''},
  {name: 'IT', url: 'it', content_type: 'team', description: ''},
  {name: 'Customer Service', url: 'cs', content_type: 'team', description: ''},
];

type Props = {
  onNext: (location: string) => unknown;
};

const SweetSuperchargeTeams = ({onNext}: Props) => {
  const [created, setCreated] = useState<Array<TeamSerializedType>>([]);
  const [suggestions, setSuggestions] =
    useState<Array<BaseItemPickerObject>>(SUGGESTED_TEAMS);

  const [createTeams] = useCreateTeams();

  function onAddTeam(team: BaseItemPickerObject) {
    return createTeams([team])
      .unwrap()
      .then((teams) => {
        setCreated((created) => [...created, ...teams]);
        setSuggestions((suggestions) =>
          suggestions.filter(
            (item) => !teams.map((team) => team.name).includes(item.name)
          )
        );
        return teams;
      });
  }

  return (
    <>
      <div className="row">
        <div className="col">
          <p className="lead">
            {gt(
              "Select any teams that you have in your company and we'll create them for you."
            )}
          </p>
        </div>
      </div>
      <div className="row">
        {suggestions.map((team) => (
          <div key={team.url} className="col-sm col-md-4">
            <SuggestedTeamCard team={team} onAdd={onAddTeam} />
          </div>
        ))}
      </div>
      <div className="row">
        {created.map((team) => (
          <div key={team.url} className="col-sm col-md-4">
            <CreatedTeamCard team={team} />
          </div>
        ))}
      </div>
      <div className="row mt-4 justify-content-end align-items-center">
        <SmallTextSpan className="col-sm-auto">
          {gt(
            'Teams control who can see your procedures, processes and policies.'
          )}
        </SmallTextSpan>
        <div className="col-sm-auto">
          <LazyTooltip
            tooltip={gt('Start using SweetProcess without creating any teams.')}
          >
            <button
              className="btn text-secondary btn-link mr-2"
              onClick={() => onNext(HOME_WELCOME_INVITATIONS)}
            >
              {gt('Skip')}
            </button>
          </LazyTooltip>
          <button
            disabled={Object.values(created).length === 0}
            className="btn btn btn-primary"
            onClick={() => onNext(HOME_WELCOME_INVITATIONS)}
          >
            {gt('Next')}
          </button>
        </div>
      </div>
    </>
  );
};

interface TeamCardProps {
  team: BaseItemPickerObject;
  onAdd: (team: BaseItemPickerObject) => Promise<any>;
}

function SuggestedTeamCard({team, onAdd}: TeamCardProps) {
  return (
    <div className="card shadow-sm mb-3">
      <div className="card-body d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="far fa-users mr-2 text-muted" /> {gt(team.name)}
        </h5>
        <StateButton
          className="btn btn-link p-0 text-decoration-none"
          busyText={gt('Adding team...')}
          onClick={() => onAdd(team)}
        >
          {gt('Add Team')}
        </StateButton>
      </div>
    </div>
  );
}

interface CreatedTeamProps {
  team: TeamSerializedType;
}

function CreatedTeamCard({team}: CreatedTeamProps) {
  const [added, setAdded] = useState<Array<DocumentSerializedType>>([]);

  const [bulkAddDocumentsToTeam] = useBulkAddDocumentsToTeam();
  const [bulkRemoveDocumentsFromTeam] = useBulkRemoveDocumentsFromTeam();

  function onAddDocument(document: DocumentSerializedType) {
    if (added.some((doc) => doc.id === document.id)) {
      return Promise.resolve(true);
    }
    setAdded((added) => [...added, document]);
    return bulkAddDocumentsToTeam({
      documents: [document.url],
      team: team.url,
    }).unwrap();
  }

  function onRemoveDocument(document: DocumentSerializedType) {
    setAdded((added) => added.filter((doc) => doc.id !== document.id));
    return bulkRemoveDocumentsFromTeam({
      documents: [document.url],
      team: team.url,
    }).unwrap();
  }

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="far fa-users mr-2 text-muted" /> {gt(team.name)}
          </h5>
          <button disabled className="btn btn-link p-0 text-decoration-none">
            {gt('Added')}
          </button>
        </div>
      </div>
      <div className="card-body">
        <h5 className="card-title d-flex align-items-center justify-content-between">
          <AddNewTeamMemberButton
            team={team}
            className="btn btn-link p-0"
            initialGiveManagerAccess={true}
            title={gt('Add Manager')}
          />
          <AddNewTeamMemberButton
            team={team}
            className="btn btn-link p-0"
            title={gt('Add Member')}
          />
        </h5>
        <StyledNoMembersMessageDiv>
          <TeamMembersSubPage
            team={team}
            onFetch={() => null}
            optionsClassName="d-none"
            isShowingRoles={false}
          />
        </StyledNoMembersMessageDiv>
        <div className="border-top py-3">
          <h5>{gt('Add documents to this team')}</h5>

          <SelectBaseRTK<DocumentSerializedType, DocumentFetchParamsType>
            isMulti={false}
            isRTKFetcher={true}
            onChange={onAddDocument}
            selected={null}
            placeholder={'Select a procedure or policy to add...'}
            typeIcon={(doc) => getContentType(doc.document_content_type).icon}
            useDataHook={useDocuments}
            paramsConverter={convertLocationParamsToFetchParams}
            selectProps={{
              createOptionPosition: 'first',
            }}
          />

          <ul className="list-group mt-4">
            {added.map((doc) => (
              <DocumentListItem
                key={doc.url}
                name={doc.name}
                isError={false}
                isLoading={false}
                ct={getContentType(doc.document_content_type)}
              >
                <button
                  className="btn btn-link btn-sm text-secondary"
                  onClick={() => onRemoveDocument(doc)}
                >
                  <i className="fa fa-times"></i>
                </button>
              </DocumentListItem>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const StyledNoMembersMessageDiv = styled.div`
  h3 {
    font-size: 1rem;
  }
`;
